/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { convertDateToEpoch } from "../../../../helper/DateConverters";

const CalendarDateSelector = ({ setDateRange, disabled = false }: any) => {
  const toUTC = (date: Date) =>
    new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    );
  const getCurrentWeekRange = () => {
    const now = new Date();
    const day = now.getDay() || 7; // Sunday=0, so treat as 7
    const monday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - day + 1,
      0,
      0,
      0,
      0
    );
    const sunday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - day + 7,
      23,
      59,
      59,
      999
    );
    return [monday, sunday];
  };

  const getCurrentWeekRangeUTC = () => {
    const now = new Date();
    const day = now.getUTCDay() || 7; // Sunday=0, so treat as 7
    const monday = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - day + 1,
        0,
        0,
        0,
        0
      )
    );

    const sunday = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - day + 7,
        23,
        59,
        59,
        999
      )
    );
    return [monday, sunday];
  };
  const [dates, setDates] = useState<any>(() => getCurrentWeekRange());
  const [tempDates, setTempDates] = useState<any>(() => {
    const [monday, sunday] = getCurrentWeekRange();
    // Ensure both are UTC dates (already set in getCurrentWeekRangeUTC)
    return [monday, sunday];
  });
  const [showPicker, setShowPicker] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const toggleRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [activeQuickRange, setActiveQuickRange] = useState<any>("Current Week");
  // const today = new Date();

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Function to check if selected dates match any quick range
  const checkForMatchingQuickRange = (
    selectedStart: Date,
    selectedEnd: Date
  ) => {
    const quickRanges = [
      "Today",
      "Yesterday",
      "Current Week",
      "Current Month",
      "Current Year",
      "Previous Week",
      "Previous Month",
      "Previous Year",
      "Last one Quarter",
      "Last one Year",
    ];

    for (const label of quickRanges) {
      const now = new Date();
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      );
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0
      );

      switch (label) {
        case "Yesterday":
          start.setDate(start.getDate() - 1);
          end.setDate(end.getDate() - 1);
          break;
        case "Current Week": {
          const day = now.getDay() || 7;
          start.setDate(now.getDate() - day + 1);
          start.setHours(0, 0, 0, 0);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case "Current Month":
          start.setDate(1);
          start.setHours(0, 0, 0, 0);
          end.setMonth(start.getMonth() + 1, 0);
          end.setHours(23, 59, 59, 999);
          break;
        case "Previous Week": {
          // Calculate previous week's Monday and Sunday
          const day = now.getDay() || 7;
          // Previous week's Monday
          start.setDate(now.getDate() - day - 6);
          start.setHours(0, 0, 0, 0);
          // Previous week's Sunday
          end.setDate(now.getDate() - day);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case "Previous Month":
          start.setMonth(start.getMonth() - 1);
          start.setDate(1);
          start.setHours(0, 0, 0, 0);
          end.setMonth(end.getMonth() - 1);
          end.setDate(
            new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
          );
          end.setHours(23, 59, 59, 999);
          break;
        case "Last one Quarter": {
          start.setMonth(start.getMonth() - 3);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case "Current Year":
          start.setMonth(0, 1);
          start.setHours(0, 0, 0, 0);
          end.setMonth(11, 31);
          end.setHours(23, 59, 59, 999);
          break;
        case "Previous Year":
          start.setFullYear(start.getFullYear() - 1, 0, 1);
          start.setHours(0, 0, 0, 0);
          end.setFullYear(end.getFullYear() - 1, 11, 31);
          end.setHours(23, 59, 59, 999);
          break;
        case "Last one Year":
          start.setFullYear(start.getFullYear() - 1);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          break;
        case "Today":
        default:
          // Today: already set
          break;
      }

      // Compare dates (normalize to same format for comparison)
      const normalizeDate = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      };

      const selectedStartNorm = normalizeDate(selectedStart);
      const selectedEndNorm = normalizeDate(selectedEnd);
      const quickStartNorm = normalizeDate(start);
      const quickEndNorm = normalizeDate(end);

      if (
        selectedStartNorm.getTime() === quickStartNorm.getTime() &&
        selectedEndNorm.getTime() === quickEndNorm.getTime()
      ) {
        return label;
      }
    }
    return null;
  };

  const handleCancel = () => {
    setDates(() => getCurrentWeekRange());
    setTempDates(() => getCurrentWeekRange());
    setDateRange(() => getCurrentWeekRangeUTC());
    setActiveQuickRange("Current Week"); // Reset to Current Week as default
    setShowPicker(false);
  };

  const setQuickRange = (start: Date, end: Date, label?: string) => {
    setTempDates([start, end]);
    setDates([start, end]);
    setShowPicker(false); // ✅ Close the picker
    if (label) {
      setActiveQuickRange(label);
    }
  };

  useEffect(() => {
    if (showPicker && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      const panelWidth = 995;
      const panelHeight = 400;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      let top = rect.bottom + scrollTop + 8;
      let left = rect.left + scrollLeft;

      // Prevent right overflow
      if (left + panelWidth > viewportWidth) {
        left = viewportWidth - panelWidth - 20;
      }

      // Prevent bottom overflow
      if (rect.bottom + panelHeight > viewportHeight) {
        top = rect.top + scrollTop - panelHeight - 8;
      }

      setPanelStyle({
        position: "absolute",
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
        width: `${panelWidth}px`,
      });
    }
  }, [showPicker]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target as Node)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (dates[0] && dates[1]) {
      // Set startDate to 00:00:00

      const adjustedStartDate = toUTC(dates[0]);

      // Set endDate to 23:59:59
      const adjustedEndDate = toUTC(dates[1]);
      adjustedEndDate.setUTCHours(23, 59, 59, 999);

      setDateRange((prevFilters: any) => ({
        ...prevFilters,
        startDate: convertDateToEpoch(adjustedStartDate),
        endDate: convertDateToEpoch(adjustedEndDate),
      }));
    }
  }, [dates]);

  return (
    <>
      {/* Toggle Button */}
      <div
        className={`tw-relative tw-inline-block tw-z-[10] ${
          disabled ? "tw-opacity-50 tw-pointer-events-none" : ""
        }`}
      >
        <div
          ref={toggleRef}
          className="tw-flex tw-items-center tw-gap-2 tw-border tw-border-solid tw-ml-2 tw-rounded-full tw-px-4 tw-py-2 tw-cursor-pointer tw-bg-white tw-shadow-sm tw-border-[#1D5D9B]"
          onClick={() => !disabled && setShowPicker((prev) => !prev)}
        >
          <i className="pi pi-calendar tw-text-[#1D5D9B]" />
          <span className="tw-text-sm">
            {dates[0] && dates[1]
              ? `${formatDate(dates[0])} - ${formatDate(dates[1])}`
              : "Select Date Range"}
          </span>

          <i
            className={`pi ${
              showPicker ? "pi-chevron-up" : "pi-chevron-down"
            } tw-ml-auto`}
          />
        </div>
      </div>

      {/* Floating Panel */}
      {showPicker && !disabled && (
        <div
          ref={panelRef}
          className="tw-absolute tw-bg-white tw-border-[0.5px] tw-border-solid tw-rounded-lg tw-shadow-2xl tw-border-gray-400  tw-flex tw-overflow-hidden tw-animate-fade-in"
          style={panelStyle}
        >
          {/* Sidebar */}
          <div className="tw-w-44 tw-border-r  tw-bg-[#DFF1FF] tw-flex tw-flex-col tw-justify-between">
            <div className="tw-p-3">
              <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-2">
                Quick Ranges
              </div>
              {[
                "Today",
                "Yesterday",
                "Current Week",
                "Current Month",
                "Current Year",
                "Previous Week",
                "Previous Month",
                "Previous Year",
                "Last one Quarter",
                "Last one Year",
              ].map((label, i) => {
                const handler = () => {
                  // Always use UTC for calculations
                  const now = new Date();
                  const end = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    23,
                    59,
                    59,
                    999
                  );
                  const start = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    0,
                    0,
                    0,
                    0
                  );

                  switch (label) {
                    case "Yesterday":
                      start.setDate(start.getDate() - 1);
                      end.setDate(end.getDate() - 1);
                      break;
                    case "Current Week": {
                      // Monday to Sunday of current week ()
                      const day = now.getDay() || 7;
                      start.setDate(now.getDate() - day + 1);
                      start.setHours(0, 0, 0, 0);
                      end.setDate(start.getDate() + 6);
                      end.setHours(23, 59, 59, 999);
                      break;
                    }
                    case "Current Month":
                      start.setDate(1);
                      start.setHours(0, 0, 0, 0);
                      end.setMonth(start.getMonth() + 1, 0);
                      end.setHours(23, 59, 59, 999);
                      break;
                    case "Previous Week": {
                      // const day = now.getDay() || 7;
                      // end.setDate(now.getDate() - day);
                      // end.setHours(23, 59, 59, 999);
                      // start.setDate(end.getDate() - 6);
                      // start.setHours(0, 0, 0, 0);
                      // break;
                      // Calculate previous week's Monday and Sunday
                      const day = now.getDay() || 7;
                      // Previous week's Monday
                      start.setDate(now.getDate() - day - 6);
                      start.setHours(0, 0, 0, 0);
                      // Previous week's Sunday
                      end.setDate(now.getDate() - day);
                      end.setHours(23, 59, 59, 999);
                      break;
                    }
                    case "Previous Month":
                      start.setMonth(start.getMonth() - 1);
                      start.setDate(1);
                      start.setHours(0, 0, 0, 0);
                      end.setMonth(end.getMonth() - 1);
                      end.setDate(
                        new Date(
                          end.getFullYear(),
                          end.getMonth() + 1,
                          0
                        ).getDate()
                      );
                      end.setHours(23, 59, 59, 999);
                      break;
                    case "Last one Quarter": {
                      // Last 3 months from today (rolling, )
                      start.setMonth(start.getMonth() - 3);
                      start.setHours(0, 0, 0, 0);
                      end.setHours(23, 59, 59, 999);
                      break;
                    }
                    case "Current Year":
                      start.setMonth(0, 1);
                      start.setHours(0, 0, 0, 0);
                      end.setMonth(11, 31);
                      end.setHours(23, 59, 59, 999);
                      break;
                    case "Previous Year":
                      start.setFullYear(start.getFullYear() - 1, 0, 1);
                      start.setHours(0, 0, 0, 0);
                      end.setFullYear(end.getFullYear() - 1, 11, 31);
                      end.setHours(23, 59, 59, 999);
                      break;
                    case "Last one Year":
                      start.setFullYear(start.getFullYear() - 1);
                      start.setHours(0, 0, 0, 0);
                      end.setHours(23, 59, 59, 999);
                      break;
                    case "Today":
                    default:
                      // Today: already set in UTC
                      break;
                  }
                  setQuickRange(start, end, label);
                };

                return (
                  <button
                    key={i}
                    className={`tw-w-full tw-cursor-pointer tw-text-left tw-text-base tw-px-2 tw-py-1 tw-border-none tw-bg-transparent hover:tw-text-[#9C1C21] ${
                      activeQuickRange === label ? "!tw-text-[#9C1C21]" : ""
                    }`}
                    onClick={
                      !disabled
                        ? () => {
                            handler();
                            // setActiveQuickRange(label);
                          }
                        : undefined
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Reset Button Below Sidebar */}
            <span
              className="tw-w-full tw-text-center tw-mb-3 tw-cursor-pointer  tw-font-bold tw-text-[#1D5D9B] hover:tw-text-[#5d88b3] tw-text-sm "
              onClick={!disabled ? handleCancel : undefined}
            >
              Reset
            </span>
          </div>

          {/* Calendar */}
          <div className="">
            <Calendar
              value={tempDates}
              onChange={(e) => {
                if (!disabled) {
                  const value = e.value as Date[];
                  setTempDates([value[0], value[1]]);
                  if (value[0] && value[1]) {
                    setDates([value[0], value[1]]);
                    // Check if the manually selected range matches any quick range
                    const matchingQuickRange = checkForMatchingQuickRange(
                      value[0],
                      value[1]
                    );
                    setActiveQuickRange(matchingQuickRange);
                    setShowPicker(false);
                  }
                }
              }}
              selectionMode="range"
              numberOfMonths={2}
              inline
              dateFormat="dd M yy"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarDateSelector;
